import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { handleOAuthCallback } from '../services/authService';
import { Loader2 } from 'lucide-react';
import { toast } from "sonner";

const AuthCallback = () => {
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    
    useEffect(() => {
        const processOAuthRedirect = async () => {
            try {
                // Get token from URL hash or query parameters
                const params = new URLSearchParams(location.search);
                const hash = location.hash.substring(1); // Remove the leading #
                const hashParams = new URLSearchParams(hash);
                
                // Try to get token from different sources
                const token = 
                    params.get('access_token') || 
                    hashParams.get('access_token') || 
                    params.get('token') ||
                    hashParams.get('token');
                
                if (!token) {
                    throw new Error('No token found in the OAuth callback URL');
                }

                // Get CSRF token from URL query parameters 
                const csrfToken = params.get('csrf') || hashParams.get('csrf');
                
                // Process the token
                const success = handleOAuthCallback(token, csrfToken || undefined);
                
                if (success) {
                    // Redirect to the saved redirect path or home page (feed)
                    const redirectPath = localStorage.getItem('redirect_after_login') || '/';
                    localStorage.removeItem('redirect_after_login');
                    
                    // If redirectPath is something like '/login' or '/signin', redirect to feed instead
                    const finalPath = redirectPath === '/login' || redirectPath === '/signin' ? '/' : redirectPath;
                    
                    toast.success("Successfully signed in!");
                    navigate(finalPath);
                } else {
                    throw new Error('Failed to process authentication token');
                }
            } catch (error) {
                console.error('OAuth callback error:', error);
                setError('Authentication failed. Please try again.');
                toast.error("Authentication failed. Please try again.");
                
                // Redirect to login page after a delay
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            }
        };

        processOAuthRedirect();
    }, [location, navigate]);

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen p-4">
                <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-md">
                    <h1 className="text-xl font-bold text-center text-red-600 mb-4">Authentication Error</h1>
                    <p className="text-gray-700 text-center mb-4">{error}</p>
                    <p className="text-gray-500 text-center text-sm">Redirecting to login page...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
            <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-md text-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
                <h1 className="text-xl font-bold mb-2">Processing Authentication</h1>
                <p className="text-gray-600">Please wait while we complete your authentication...</p>
            </div>
        </div>
    );
};

export default AuthCallback;
